import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DataExtensionField } from '../../../../../lib/common/custom-activity/dataExtensionTypes';
import {
    Activity,
    Schema,
} from '../../../../../lib/common/custom-activity/eventTypes';
import { trigger } from '../../../../../lib/common/postmonger/postmongerManager';

export interface SimpleConfigurationPageState {
    profileType: string;

    dirty: boolean;
}

const initialState: SimpleConfigurationPageState = {
    profileType: 'Individual',

    dirty: false,
};

interface DoneArgs {
    activity: Activity;
    schema: Schema;
}

export const simpleConfigurationPageSlice = createSlice({
    name: 'simpleConfigurationPage',
    initialState,
    reducers: {
        activityUpdated: (
            state: SimpleConfigurationPageState,
            action: PayloadAction<Activity>,
        ) => {
            const activity = action.payload;

            // If we ever get an activity update during configuration, we can't know
            // what the state really is. We should just reset.
            state.dirty = false;

            // Set the profile type dropdown to the value provided in the activity. If
            // the profile type hasn't already been set in the activity, also dirty
            // the state so that the user can save it.
            let profileType = getInArgumentValue(activity, 'profileType');
            if (profileType === null) {
                state.dirty = true;
                profileType = 'Individual';
            }
            state.profileType = profileType;
        },
        profileSelectionChanged: (
            state: SimpleConfigurationPageState,
            action: PayloadAction<string>,
        ) => {
            const profileType = action.payload;

            if (state.profileType !== profileType) {
                state.profileType = profileType;

                state.dirty = true;
                trigger('setActivityDirtyState', true);
            }
        },
        done: (
            state: SimpleConfigurationPageState,
            action: PayloadAction<DoneArgs>,
        ) => {
            // Build a new activity with the updated data
            const activity = JSON.parse(
                JSON.stringify(action.payload.activity),
            );
            const schema = action.payload.schema;

            if (!state.dirty) {
                console.warn('Nothing to save.');
                return;
            }

            if (activity === null || schema === null) {
                const nullField = activity === null ? 'Activity' : 'Schema';
                console.error(
                    `Unable to finalize activity. ${nullField} is not defined.`,
                );
                return;
            }

            const IdEntryKey = getFieldKeyFromSchema(schema, 'Id');
            const dataSourceObjectIdEntryKey = getFieldKeyFromSchema(
                schema,
                'DataSourceObjectId',
            );
            if (IdEntryKey === null || dataSourceObjectIdEntryKey === null) {
                const nullField =
                    IdEntryKey === null ? 'IdEntryKey' : 'DataSourceObjectId';
                console.error(
                    `Unable to finalize activity. ${nullField} is not defined in activity.`,
                );
                return;
            }

            addOrUpdateInArgument(activity, 'id', '{{' + IdEntryKey + '}}');
            addOrUpdateInArgument(
                activity,
                'dataSourceObjectId',
                '{{' + dataSourceObjectIdEntryKey + '}}',
            );
            addOrUpdateInArgument(activity, 'profileType', state.profileType);

            activity.metaData['isConfigured'] = true;

            trigger('updateActivity', activity);
            trigger('requestInspectorClose');
        },
        cancel: (state: SimpleConfigurationPageState) => {
            // Tell Journey Builder that this activity has no changes.
            // we wont be prompted to save changes when the inspector closes
            trigger('setActivityDirtyState', false);

            // now request that Journey Builder closes the inspector/drawer
            trigger('requestInspectorClose');
        },
    },
});

export const { activityUpdated, profileSelectionChanged, done, cancel } =
    simpleConfigurationPageSlice.actions;

function findField(schema: Schema, key: string): DataExtensionField | null {
    return schema.find((field) => field.name === key) || null;
}

function getFieldKeyFromSchema(schema: Schema, key: string): string | null {
    if (schema === null) {
        return null;
    }

    const field = findField(schema, key);
    if (field === null) {
        return null;
    }
    return field.key;
}

function getInArgumentValue(
    activity: Activity,
    argument: string,
): string | null {
    const inArguments = activity?.arguments?.execute?.inArguments;
    if (inArguments === undefined) {
        return null;
    }

    // See if we already have an argument with this name. We'll update the value if so.
    let executeArg = inArguments.find((item: any) => argument in item);

    // Bail if the argument doesn't exist
    if (executeArg === undefined || !(argument in executeArg)) {
        return null;
    }

    return executeArg[argument];
}

/**
 * Adds or updates an inArgument with the name `argument` and value `value`.
 * @param activity The activity to update
 * @param argument The argument name
 * @param value The argument value
 * @returns Whether anything was changed.
 */
function addOrUpdateInArgument(
    activity: Activity,
    argument: string,
    value: string,
): boolean {
    if (activity.arguments === undefined) {
        activity.arguments = {};
    }

    if (activity.arguments.execute === undefined) {
        activity.arguments.execute = {};
    }

    if (activity.arguments.execute.inArguments === undefined) {
        activity.arguments.execute.inArguments = [];
    }

    const inArguments = activity.arguments.execute.inArguments;

    // See if we already have an argument with this name. We'll update the value if so.
    let executeArg = inArguments.find((item: any) => argument in item);

    // Bail if we don't need to change anything
    if (
        executeArg !== undefined &&
        argument in executeArg &&
        executeArg[argument] === value
    ) {
        return false;
    }

    if (executeArg === undefined) {
        executeArg = {};
        inArguments.push(executeArg);
    }

    executeArg[argument] = value;
    return true;
}
